import { ceilToMultiple, tryParseDuration, tryParseSpan } from './duration';
import type { Aggregate, DesignConfig } from './types';

/** A duration field that could not be parsed, addressed by a dotted path. */
export interface ParseError {
  /** e.g. `source.interval` or `tiers.1.window`. */
  path: string;
  /** Human label for the field, used in messages. */
  label: string;
  /** The offending literal. */
  value: string;
}

/** Everything a tier needs, with durations resolved and the cascade wired up. */
export interface ResolvedTier {
  /** Position in the cascade, 0-based. */
  index: number;
  id: string;
  bucket: string;
  window: string;
  windowSeconds: number;
  retentionSeconds: number;
  aggregates: Aggregate[];
  createEmpty: boolean;

  /** Bucket this tier reads from — raw for tier 0, the previous tier otherwise. */
  sourceBucket: string;
  /** Sample resolution of the input: the raw interval, or the parent window. */
  sourceResolutionSeconds: number;
  /** Retention of the input bucket, used to spot cascade gaps. */
  sourceRetentionSeconds: number;
  /** Aggregates already applied upstream — empty for tier 0. */
  sourceAggregates: Aggregate[];
  /** True when this tier reads the raw bucket. */
  fromRaw: boolean;

  /** Effective task `every` — the schedule override, or the tier window. */
  everySeconds: number;
  offsetSeconds: number;
  /**
   * How far back the generated query reaches, rounded up to a whole number of
   * windows so a partially filled window is never written twice.
   */
  lookbackSeconds: number;
  /** Task name derived from the destination bucket. */
  taskName: string;
}

export interface ResolvedPlan {
  source: {
    bucket: string;
    org: string;
    measurement: string;
    fields: string[];
    intervalSeconds: number;
    retentionSeconds: number;
    /** Distinct series in the raw bucket: every tag combination × every field. */
    seriesCount: number;
    /** Series that survive into a rollup, i.e. only retained tags contribute. */
    retainedSeriesCount: number;
  };
  tiers: ResolvedTier[];
  parseErrors: ParseError[];
  /** True when every duration parsed and the plan can be costed and generated. */
  valid: boolean;
}

function seriesFor(config: DesignConfig, onlyRetained: boolean): number {
  const fields = Math.max(1, config.source.fields.filter((f) => f.trim() !== '').length);
  const tags = config.source.tagKeys.filter(
    (t) => t.key.trim() !== '' && (!onlyRetained || t.retained),
  );
  return tags.reduce((acc, t) => acc * Math.max(1, t.cardinality), fields);
}

/** Turn a bucket name into a valid, readable task name. */
export function taskNameFor(bucket: string, measurement: string): string {
  const slug = (s: string) =>
    s
      .trim()
      .replace(/[^A-Za-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  const parts = [slug(measurement) || 'measurement', slug(bucket) || 'rollup'];
  return `downsample_${parts.join('_to_')}`.toLowerCase();
}

/**
 * Resolve a raw {@link DesignConfig} into the derived shape the generator, the
 * storage model and the warning rules all consume.
 *
 * Durations that fail to parse are collected into {@link ResolvedPlan.parseErrors}
 * and substituted with a neutral fallback, so the UI can keep rendering while the
 * user is mid-edit instead of blanking out on every keystroke.
 */
export function resolvePlan(config: DesignConfig): ResolvedPlan {
  const parseErrors: ParseError[] = [];

  const parse =
    (parser: (value: string) => number | null) =>
    (value: string, path: string, label: string, fallback: number): number => {
      const parsed = parser(value);
      if (parsed === null) {
        parseErrors.push({ path, label, value });
        return fallback;
      }
      return parsed;
    };
  // Retention-like fields treat `0s` as infinite; schedule spans treat it as zero.
  const duration = parse(tryParseDuration);
  const span = parse(tryParseSpan);

  const intervalSeconds = duration(
    config.source.interval,
    'source.interval',
    'raw sample interval',
    10,
  );
  const sourceRetention = duration(
    config.source.retention,
    'source.retention',
    'raw bucket retention',
    7 * 86400,
  );
  const lateTolerance = span(
    config.schedule.lateTolerance,
    'schedule.lateTolerance',
    'late-data tolerance',
    0,
  );
  const offsetSeconds = span(config.schedule.offset, 'schedule.offset', 'task offset', 0);
  const everyOverride =
    config.schedule.every.trim() === ''
      ? null
      : duration(config.schedule.every, 'schedule.every', 'task every', 60);

  let upstreamResolution = intervalSeconds;
  let upstreamRetention = sourceRetention;
  let upstreamBucket = config.source.bucket;
  let upstreamAggregates: Aggregate[] = [];

  const tiers: ResolvedTier[] = config.tiers.map((tier, index) => {
    const windowSeconds = duration(
      tier.window,
      `tiers.${index}.window`,
      `tier ${index + 1} window`,
      60,
    );
    const retentionSeconds = duration(
      tier.retention,
      `tiers.${index}.retention`,
      `tier ${index + 1} retention`,
      30 * 86400,
    );
    const everySeconds = everyOverride ?? windowSeconds;
    const lateWindow = Number.isFinite(lateTolerance) ? lateTolerance : 0;
    const resolved: ResolvedTier = {
      index,
      id: tier.id,
      bucket: tier.bucket,
      window: tier.window,
      windowSeconds,
      retentionSeconds,
      aggregates: tier.aggregates.length > 0 ? tier.aggregates : ['mean'],
      createEmpty: tier.createEmpty,
      sourceBucket: upstreamBucket,
      sourceResolutionSeconds: upstreamResolution,
      sourceRetentionSeconds: upstreamRetention,
      sourceAggregates: upstreamAggregates,
      fromRaw: index === 0,
      everySeconds,
      offsetSeconds,
      lookbackSeconds: ceilToMultiple(everySeconds + lateWindow, windowSeconds),
      taskName: taskNameFor(tier.bucket, config.source.measurement),
    };

    upstreamResolution = windowSeconds;
    upstreamRetention = retentionSeconds;
    upstreamBucket = tier.bucket;
    upstreamAggregates = resolved.aggregates;
    return resolved;
  });

  return {
    source: {
      bucket: config.source.bucket,
      org: config.source.org,
      measurement: config.source.measurement,
      fields: config.source.fields.filter((f) => f.trim() !== ''),
      intervalSeconds,
      retentionSeconds: sourceRetention,
      seriesCount: seriesFor(config, false),
      retainedSeriesCount: seriesFor(config, true),
    },
    tiers,
    parseErrors,
    valid: parseErrors.length === 0,
  };
}
